import {
  type DependencyList,
  type EffectCallback,
  useEffect,
  useRef,
} from "react";
import Resizer from "react-image-file-resizer";
import { type ImageSourcePropType, Platform } from "react-native";
import { PlaniraPlanImage } from "../../templates/types";

export function isRunningInApp(): boolean {
  // @ts-expect-error
  return !!window.__appUser;
}

export function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

export function assertNever(x: never): never {
  throw new Error(`Unexpected Value: ${x}`);
}

export function getRandomInt(min: number, max: number) {
  const ceilMin = Math.ceil(min);
  const floorMax = Math.floor(max);
  return Math.floor(Math.random() * (floorMax - ceilMin + 1) + ceilMin); // The maximum is inclusive and the minimum is inclusive
}

export function getImageInfo(url: string) {
  return {
    50: url.replace("?", "_50x50?"),
    128: url.replace("?", "_128x128?"),
    400: url.replace("?", "_400x400?"),
    token: url.split("&token=")[1] ?? "",
  };
}

export const resizeAvatarFile = (file: File): Promise<Blob> =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      128,
      128,
      "WEBP",
      100,
      0,
      (blob) => {
        resolve(blob as Blob);
      },
      "blob",
    );
  });

export const resizeCoverFile = (file: Blob): Promise<Blob> =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      600,
      600,
      "WEBP",
      100,
      0,
      (blob) => {
        resolve(blob as Blob);
      },
      "blob",
    );
  });

export const resizeDocFile = (file: Blob): Promise<Blob> =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      600,
      600,
      "WEBP",
      100,
      0,
      (blob) => {
        resolve(blob as Blob);
      },
      "blob",
    );
  });

export const arrayMove = <T>(arr: T[], fromIndex: number, toIndex: number) => {
  const newArr = [...arr];
  newArr.splice(toIndex, 0, newArr.splice(fromIndex, 1)[0]);
  return newArr;
};

// biome-ignore lint/suspicious/noExplicitAny: Because
const usePrevious = (value: any, initialValue: any) => {
  const ref = useRef(initialValue);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useEffectDebugger = (
  effectHook: EffectCallback,
  dependencies: DependencyList,
  dependencyNames = [],
) => {
  const previousDeps = usePrevious(dependencies, []);

  const changedDeps = dependencies.reduce<{
    [key: string]: { before: unknown; after: unknown };
  }>((accum, dependency, index) => {
    if (dependency !== previousDeps[index]) {
      const keyName = dependencyNames[index] || index;
      accum[keyName] = {
        before: previousDeps[index],
        after: dependency,
      };

      return accum;
    }

    return accum;
  }, {});

  if (Object.keys(changedDeps).length) {
    console.log("[use-effect-debugger] ", changedDeps);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: Safeish
  useEffect(effectHook, dependencies);
};

export function getAverage<T>(
  items: T[],
  getNumber: (item: T) => number,
): number {
  if (!items.length) {
    return 0;
  }

  return (
    items.reduce<number>((total, current) => total + getNumber(current), 0) /
    items.length
  );
}

const NUM_TRIM_ITEMS = 3;

export function stringTrimList(input?: string[]): string {
  if (!input?.length) {
    return "";
  }

  if (input.length < NUM_TRIM_ITEMS) {
    return input.join(", ");
  }

  const numRest = input.length - NUM_TRIM_ITEMS;

  return (
    input.slice(0, NUM_TRIM_ITEMS).join(", ") +
    (numRest > 0 ? ` + ${numRest} more` : "")
  );
}

export const isIos = () => {
  if (Platform.OS !== "web") {
    return Platform.OS === "ios";
  }

  const platform = window.navigator.platform;
  const iosPlatforms = ["iPhone", "iPad", "iPod"];

  return iosPlatforms.indexOf(platform) !== -1;
};

export function getLanguage() {
  if (navigator.languages !== undefined) {
    return navigator.languages[0];
  }

  return navigator?.language ?? "en-US";
}

export const PlaniraImageLookUp: {
  [key in PlaniraPlanImage]: ImageSourcePropType;
} = {
  [PlaniraPlanImage.Calendar]: require("../../assets/illustrations/Calendar-cuate.svg"),
  [PlaniraPlanImage.Woodworker]: require("../../assets/illustrations/Woodworker-bro.svg"),
  [PlaniraPlanImage.Party]: require("../../assets/illustrations/bachelorparty-bro.svg"),
  [PlaniraPlanImage.Travel]: require("../../assets/illustrations/Travelers-bro.svg"),
};

export function assertUnreachable(_x: never): never {
  throw new Error("Didn't expect to get here");
}

export function isBeforeToday(eventDate: Date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of today

  eventDate.setHours(0, 0, 0, 0); // Set to start of event day

  return eventDate < today;
}
